/**
 * 일정 — 학원 모드. 월간 캘린더 + 일정 추가/삭제.
 * 날짜를 탭하면 그 날 일정 목록이 아래에 펼쳐진다.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { IoChevronBack, IoChevronForward, IoAdd, IoTrashOutline } from 'react-icons/io5';
import { useAcademy } from '../../context/AcademyContext';
import academyService from '../../services/academyService';
import BrandLoader from '../../components/common/BrandLoader';
import Badge from '../../components/common/Badge';

const WEEK = ['일', '월', '화', '수', '목', '금', '토'];
const pad = (n) => String(n).padStart(2, '0');
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const TODAY = ymd(new Date());

const buildCells = (year, month) => {
  const startDow = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startDow; i += 1) cells.push(null);
  for (let d = 1; d <= daysInMonth; d += 1) cells.push(d);
  return cells;
};

const BizSchedulePage = () => {
  const { operatedAcademy } = useAcademy();
  const aid = operatedAcademy?.id;

  const [cursor, setCursor] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [events, setEvents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(TODAY);
  const [modal, setModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ title: '', date: TODAY, time: '', memo: '' });

  const load = useCallback(async () => {
    if (!aid) { setLoading(false); return; }
    try {
      const [ev, cls] = await Promise.all([
        academyService.getEvents(aid),
        academyService.getClasses(aid),
      ]);
      setEvents(ev);
      setClasses(cls);
    } catch (e) {
      console.error('일정 로드 실패:', e);
    } finally {
      setLoading(false);
    }
  }, [aid]);

  useEffect(() => { load(); }, [load]);

  const year = cursor.getFullYear();
  const month = cursor.getMonth();

  const cells = useMemo(() => buildCells(year, month), [year, month]);
  const eventDates = useMemo(() => new Set(events.map((e) => e.date)), [events]);
  const dayEvents = useMemo(
    () => events.filter((e) => e.date === selected),
    [events, selected],
  );

  // 요일별(0=일~6=토) 수업 목록 — 캘린더 칸 표시용 (이름·색상)
  const classesByDow = useMemo(() => {
    const arr = [[], [], [], [], [], [], []];
    classes.forEach((c) => {
      const days = new Set((c.schedule || []).map((s) => s.day));
      days.forEach((dch) => {
        const idx = WEEK.indexOf(dch);
        if (idx >= 0) arr[idx].push({ id: c.id, name: c.name, color: c.color || '#4F46E5' });
      });
    });
    return arr;
  }, [classes]);

  // 선택한 날짜 요일의 수업 세션 목록
  const daySessions = useMemo(() => {
    const [y, m, d] = selected.split('-').map(Number);
    const wd = WEEK[new Date(y, m - 1, d).getDay()];
    const out = [];
    classes.forEach((c) => {
      (c.schedule || []).forEach((s) => {
        if (s.day === wd) {
          out.push({ key: `${c.id}-${s.day}-${s.time || ''}`, name: c.name, time: s.time || '', color: c.color || '#4F46E5' });
        }
      });
    });
    return out.sort((a, b) => (a.time || '').localeCompare(b.time || ''));
  }, [classes, selected]);

  const moveMonth = (delta) => setCursor(new Date(year, month + delta, 1));

  const openModal = () => {
    setForm({ title: '', date: selected, time: '', memo: '' });
    setModal(true);
  };

  const save = async () => {
    if (!form.title.trim() || !form.date || !aid) return;
    setSaving(true);
    try {
      await academyService.addEvent(aid, {
        title: form.title.trim(),
        date: form.date,
        time: form.time.trim(),
        memo: form.memo.trim(),
      });
      const [y, m] = form.date.split('-').map(Number);
      setCursor(new Date(y, m - 1, 1));
      setSelected(form.date);
      setModal(false);
      await load();
    } catch (e) {
      console.error('일정 저장 실패:', e);
      alert('일정 저장에 실패했어요.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (ev) => {
    if (!window.confirm(`'${ev.title}' 일정을 삭제할까요?`)) return;
    try {
      await academyService.deleteEvent(aid, ev.id);
      await load();
    } catch (e) {
      console.error('일정 삭제 실패:', e);
      alert('일정 삭제에 실패했어요.');
    }
  };

  const selLabel = () => {
    const [y, m, d] = selected.split('-').map(Number);
    return `${m}월 ${d}일 (${WEEK[new Date(y, m - 1, d).getDay()]})`;
  };

  if (!operatedAcademy) {
    return (
      <Container>
        <Empty><EmptyText>운영 중인 학원이 없어요.</EmptyText></Empty>
      </Container>
    );
  }
  if (loading) return <BrandLoader />;

  return (
    <Container>
      <Head>
        <div>
          <Title>일정</Title>
          <Sub>{operatedAcademy.name}</Sub>
        </div>
        <NewBtn type="button" onClick={openModal}><IoAdd size={18} /> 일정</NewBtn>
      </Head>

      <MonthBar>
        <ArrowBtn type="button" onClick={() => moveMonth(-1)}><IoChevronBack size={20} /></ArrowBtn>
        <MonthText>{year}년 {month + 1}월</MonthText>
        <ArrowBtn type="button" onClick={() => moveMonth(1)}><IoChevronForward size={20} /></ArrowBtn>
      </MonthBar>

      <Grid>
        {WEEK.map((w, i) => (
          <WeekCell key={w} $sun={i === 0} $sat={i === 6}>{w}</WeekCell>
        ))}
        {cells.map((d, i) => {
          if (d === null) return <DayCell key={`e${i}`} />;
          const dateStr = `${year}-${pad(month + 1)}-${pad(d)}`;
          const dow = i % 7;
          return (
            <DayCell key={dateStr} type="button" onClick={() => setSelected(dateStr)}>
              <DayNum
                $on={selected === dateStr}
                $today={TODAY === dateStr}
                $sun={dow === 0}
                $sat={dow === 6}
              >
                {d}
              </DayNum>
              <Marks>
                {classesByDow[dow].map((cl) => (
                  <ClassChip key={cl.id} $color={cl.color}>{cl.name}</ClassChip>
                ))}
                {eventDates.has(dateStr) && <EvDot $on={selected === dateStr} />}
              </Marks>
            </DayCell>
          );
        })}
      </Grid>

      <DayPanel>
        <DayPanelHead>{selLabel()}</DayPanelHead>
        {daySessions.length === 0 && dayEvents.length === 0 && (
          <NoEvent>등록된 일정이 없어요.</NoEvent>
        )}
        {daySessions.map((s) => (
          <EventCard key={s.key}>
            <ColorBar $color={s.color} />
            <EvMain>
              <EvTop>
                {s.time && <EvTime>{s.time}</EvTime>}
                <EvTitle>{s.name}</EvTitle>
              </EvTop>
            </EvMain>
            <Badge $size="sm">수업</Badge>
          </EventCard>
        ))}
        {dayEvents.length > 0 && (
          dayEvents.map((ev) => (
            <EventCard key={ev.id}>
              <EvMain>
                <EvTop>
                  {ev.time && <EvTime>{ev.time}</EvTime>}
                  <EvTitle>{ev.title}</EvTitle>
                </EvTop>
                {ev.memo && <EvMemo>{ev.memo}</EvMemo>}
              </EvMain>
              <DelBtn type="button" onClick={() => remove(ev)}>
                <IoTrashOutline size={17} />
              </DelBtn>
            </EventCard>
          ))
        )}
      </DayPanel>

      {modal && (
        <Overlay onClick={() => setModal(false)}>
          <Sheet onClick={(e) => e.stopPropagation()}>
            <SheetTitle>일정 추가</SheetTitle>

            <FLabel>제목</FLabel>
            <FInput
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              autoFocus
            />

            <FLabel>날짜</FLabel>
            <FInput
              type="date"
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
            />

            <FLabel>시간</FLabel>
            <TimeRow>
              <TimeSelect
                value={(form.time || '09:00').split(':')[0]}
                onChange={(e) => {
                  const m = (form.time || '09:00').split(':')[1] || '00';
                  setForm({ ...form, time: `${e.target.value}:${m}` });
                }}
              >
                {Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0')).map((h) => (
                  <option key={h} value={h}>{h}시</option>
                ))}
              </TimeSelect>
              <TimeColon>:</TimeColon>
              <TimeSelect
                value={(form.time || '09:00').split(':')[1] || '00'}
                onChange={(e) => {
                  const h = (form.time || '09:00').split(':')[0] || '09';
                  setForm({ ...form, time: `${h}:${e.target.value}` });
                }}
              >
                {['00', '10', '20', '30', '40', '50'].map((m) => (
                  <option key={m} value={m}>{m}분</option>
                ))}
              </TimeSelect>
            </TimeRow>

            <FLabel>메모</FLabel>
            <FArea
              rows={3}
              value={form.memo}
              onChange={(e) => setForm({ ...form, memo: e.target.value })}
            />

            <SheetActions>
              <CancelBtn type="button" onClick={() => setModal(false)}>취소</CancelBtn>
              <SaveBtn type="button" disabled={!form.title.trim() || !form.date || saving} onClick={save}>
                {saving ? '저장 중...' : '저장'}
              </SaveBtn>
            </SheetActions>
          </Sheet>
        </Overlay>
      )}
    </Container>
  );
};

export default BizSchedulePage;

const Container = styled.div`
  max-width: 480px;
  margin: 0 auto;
  padding: 22px 16px;
  overflow-x: hidden;
`;

const Head = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const Title = styled.p`
  font-size: 20px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Sub = styled.p`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.hint};
  margin-top: 4px;
`;

const NewBtn = styled.button`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 3px;
  padding: 9px 14px;
  border-radius: 11px;
  background: #fff;
  border: 1px solid ${({ theme }) => theme.colors.border};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 13px;
  font-weight: 700;
`;

const MonthBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 18px;
  margin-bottom: 10px;
`;

const ArrowBtn = styled.button`
  display: flex;
  background: none;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const MonthText = styled.p`
  font-size: 16px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
`;

const WeekCell = styled.div`
  padding: 8px 0;
  text-align: center;
  font-size: 12px;
  font-weight: 700;
  color: ${({ $sun, $sat, theme }) =>
    $sun ? '#EF4444' : $sat ? '#3B82F6' : theme.colors.text.hint};
`;

const DayCell = styled.button`
  height: 62px;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  gap: 3px;
  padding: 3px 2px 0;
  background: none;
  overflow: hidden;
`;

const DayNum = styled.span`
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-size: 13.5px;
  font-weight: ${({ $on, $today }) => ($on || $today ? 800 : 600)};
  color: ${({ $on, $today, $sun, $sat, theme }) => {
    if ($on) return '#fff';
    if ($today) return theme.colors.primary;
    if ($sun) return '#EF4444';
    if ($sat) return '#3B82F6';
    return theme.colors.text.primary;
  }};
  background: ${({ $on, $today, theme }) => {
    if ($on) return theme.colors.primary;
    if ($today) return theme.colors.primaryLight;
    return 'transparent';
  }};
`;

const Marks = styled.div`
  width: 100%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 2px;
`;

const ClassChip = styled.span`
  display: block;
  width: 100%;
  min-width: 0;
  padding: 1px 2px;
  font-size: 9px;
  font-weight: 800;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: center;
  color: ${({ $color, theme }) => $color || theme.colors.primary};
`;

const EvDot = styled.span`
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: ${({ $on, theme }) => ($on ? theme.colors.primary : theme.colors.accent.gold)};
`;

const ColorBar = styled.span`
  width: 4px;
  align-self: stretch;
  flex-shrink: 0;
  border-radius: 3px;
  background: ${({ $color }) => $color};
`;

const ClassTag = styled.span`
  flex-shrink: 0;
  padding: 3px 9px;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.primary};
  background: ${({ theme }) => theme.colors.primaryLight};
`;

const DayPanel = styled.div`
  margin-top: 14px;
  padding-top: 16px;
  border-top: 1px solid #EEF0F3;
`;

const DayPanelHead = styled.p`
  font-size: 15px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 10px;
`;

const NoEvent = styled.p`
  padding: 28px 0;
  text-align: center;
  font-size: 13.5px;
  color: ${({ theme }) => theme.colors.text.hint};
`;

const EventCard = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 13px 2px;
  border-bottom: 1px solid #F0F1F4;
  &:last-of-type { border-bottom: none; }
`;

const EvMain = styled.div`
  flex: 1;
  min-width: 0;
`;

const EvTop = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
`;

const EvTime = styled.span`
  flex-shrink: 0;
  font-size: 12.5px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.primary};
`;

const EvTitle = styled.span`
  font-size: 15px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const EvMemo = styled.p`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-top: 4px;
  line-height: 1.5;
`;

const DelBtn = styled.button`
  flex-shrink: 0;
  display: flex;
  color: ${({ theme }) => theme.colors.text.hint};
`;

const Empty = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 90px 0;
`;

const EmptyText = styled.p`
  font-size: 15px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 200;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: flex-end;
  justify-content: center;
`;

const Sheet = styled.div`
  width: 100%;
  max-width: 480px;
  max-height: 88vh;
  overflow-y: auto;
  background: #fff;
  border-radius: 20px 20px 0 0;
  padding: 22px 18px 28px;
`;

const SheetTitle = styled.p`
  font-size: 18px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const FLabel = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 16px 0 7px;
`;

const FInput = styled.input`
  width: 100%;
  padding: 13px 14px;
  border: 0;
  border-radius: 11px;
  background: #F4F5F7;
  font-size: 15px;
  &:focus { outline: 2px solid ${({ theme }) => theme.colors.primary}; }
`;

const TimeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TimeSelect = styled.select`
  flex: 1;
  padding: 13px 14px;
  border: 0;
  border-radius: 11px;
  background: #F4F5F7;
  font-size: 15px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  appearance: none;
  -webkit-appearance: none;
  text-align: center;
  &:focus { outline: none; }
`;

const TimeColon = styled.span`
  font-size: 17px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text.hint};
`;

const FArea = styled.textarea`
  width: 100%;
  padding: 13px 14px;
  border: 0;
  border-radius: 11px;
  background: #F4F5F7;
  font-size: 15px;
  resize: none;
  font-family: inherit;
  &:focus { outline: 2px solid ${({ theme }) => theme.colors.primary}; }
`;

const SheetActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 24px;
`;

const CancelBtn = styled.button`
  flex: 1;
  padding: 14px;
  border-radius: 12px;
  background: #F4F5F7;
  font-size: 15px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const SaveBtn = styled.button`
  flex: 2;
  padding: 14px;
  border-radius: 12px;
  background: ${({ theme }) => theme.colors.primaryGradient};
  color: #fff;
  font-size: 15px;
  font-weight: 700;
  &:disabled { background: #D7D9E0; }
`;
